<template>
  <div>
    <v-row justify="center" no-gutters>
      <v-col cols="12">
        <v-tabs v-model="tabs" background-color="transparent">
          <v-tab>
            <v-btn icon color="light-green">
              <v-icon>mdi-map-search</v-icon>
            </v-btn>
          </v-tab>

          <v-tab>
            <v-btn icon color="blue">
              <v-icon>mdi-format-list-bulleted</v-icon>
            </v-btn>
          </v-tab>

          <v-tab>
            <v-btn icon color="red">
              <v-icon>mdi-cards-heart</v-icon>
            </v-btn>
          </v-tab>

          <v-row justify="center" no-gutters>
            <v-col cols="10" offset="1">
              <v-autocomplete append-icon="mdi-magnify" v-model="location" :items="locationsFiltered" @change="onChangeLocation()" clearable hide-details hide-selected return-object :label="$i18n.t('products.station')" :filter="locationCustomFilter">
                <template slot="append">
                  <v-icon v-on:click.stop.prevent="" left>mdi-magnify</v-icon>
                </template>

                <template v-slot:selection="{ item }">
                  <div class="caption">
                    <strong>{{ item.projectName }} : {{ item.objectName }}</strong> - <i>{{ item.sn }} - {{ item.type }} </i>
                  </div>
                </template>

                <template v-slot:item="{ item }">
                  <div class="caption">
                    <strong>{{ item.projectName }} : {{ item.objectName }}</strong> - <i>{{ item.sn }} - {{ item.type }} </i>
                  </div>
                </template>
              </v-autocomplete>
            </v-col>
          </v-row>

          <v-tab>
            <v-btn icon large color="orange">
              <v-icon>mdi-filter</v-icon>
            </v-btn>
          </v-tab>

          <!-- Mapa -->
          <v-tab-item class="ma-2">
            <v-row>
              <v-col cols="12" align="center">
                <v-leaflet-map v-bind:style="{ height: mapHeight + 'px', width: mapWidth + 'px', zIndex: 0 }" :zoom="zoom" :center="center" ref="myMap" @ready="mapOnReady()" @update:zoom="zoomUpdated" @update:center="centerUpdated" @update:bounds="boundsUpdated">
                  <v-leaflet-tilelayer :noWrap="true" :url="url" :attribution="attribution"> </v-leaflet-tilelayer>
                  <v-leaflet-control position="bottomleft">
                    <v-hover v-if="showSearchButton" v-slot="{ hover }">
                      <v-btn :small="true" :plain="true" @click="triggerSearchStations">
                        <v-row justify="center">
                          <v-col cols="12">
                            <v-icon v-if="!searchButtonIsLoading" :elevation="hover ? 12 : 0" :color="hover ? 'info' : 'secondary'"> mdi-map-search </v-icon>
                            <v-progress-circular v-else indeterminate color="info" :size="24"></v-progress-circular>
                            <span v-if="!searchButtonIsLoading"> Vyhledat v této lokalitě </span>
                            <span v-else> Načítání... </span>
                          </v-col>
                        </v-row>
                      </v-btn>
                    </v-hover>
                  </v-leaflet-control>
                  <v-leaflet-control-scale position="topright" :imperial="false" :metric="true"></v-leaflet-control-scale>

                  <v-leaflet-marker-cluster :options="clusterOptions" @clusterclick="click" @ready="ready">
                    <v-leaflet-marker v-for="location in locationsFiltered" :key="location.pointIndex" :lat-lng="location.latLng" :icon="icon" @click="onClickMarker(location)">
                      <v-leaflet-icon :icon-size="dynamicSize" :icon-anchor="dynamicAnchor" :icon-url="location.icons"> </v-leaflet-icon>

                      <v-leaflet-popup>
                        <span>
                          <b>
                            <template v-if="'name' in location"> {{ location.objectName }} : {{ location.name }} <br /> </template>
                          </b>
                          <br />

                          <template v-if="location.minPower != undefined">
                            <template v-if="location.minPower != 0"> {{ $t("products.table.minPower") }}: {{ location.minPower }}<br /> </template>
                          </template>
                          <template v-if="location.powerType != undefined"> {{ $t("products.table.powerType") }}: {{ location.powerType }}<br /> </template>
                          <template v-if="location.plugType != undefined"> {{ $t("products.table.plugType") }}: {{ location.plugType }}<br /> </template>

                          {{ $t("products.table.serialNumber") }}: {{ location.sn }} <br /><br />

                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn class="mx-2" fab dark x-small color="primary" @click="setPoint(location)" v-bind="attrs" v-on="on">
                                <v-icon dark> mdi-location-enter </v-icon>
                              </v-btn>
                            </template>
                            <span>{{ $t("products.actions.enter") }}</span>
                          </v-tooltip>

                          <v-tooltip bottom>
                            <template v-slot:activator="{ on, attrs }">
                              <v-btn class="mx-2" fab dark x-small @click="addToFavorites(location, favorites)" :color="getFavoriteColor(location, favorites)" v-bind="attrs" v-on="on">
                                <v-icon dark> mdi-cards-heart </v-icon>
                              </v-btn>
                            </template>
                            <span>{{ $t("products.actions.favorite") }}</span>
                          </v-tooltip>
                        </span>
                      </v-leaflet-popup>
                    </v-leaflet-marker>
                  </v-leaflet-marker-cluster>

                  <v-leaflet-circle :lat-lng="myLocation.gps" :radius="myLocation.radius" :color="myLocation.color">
                    <v-leaflet-tooltip> {{ $t("products.myLocation") }} {{ myLocation.accuracy }} m </v-leaflet-tooltip>
                  </v-leaflet-circle>
                </v-leaflet-map>
              </v-col>
            </v-row>
          </v-tab-item>

          <!-- Tabulka -->
          <v-tab-item class="ma-2">
            <ProductsSimpleTable :items="locationsFiltered" @eventSetPoint="handleSetPoint" @eventAddToFavorite="handleAddToFavorite" />
          </v-tab-item>

          <!-- Oblíbené -->
          <v-tab-item class="ma-2">
            <ProductsSimpleTable :items="favorites" @eventSetPoint="handleSetPoint" @eventAddToFavorite="handleAddToFavorite" />
          </v-tab-item>

          <!-- Filtry -->
          <v-tab-item class="ma-2">
            <v-row>
              <v-col cols="8"
                ><h2>{{ $t("products.filters.header") }}</h2></v-col
              >
              <v-col align="end">
                {{ $t("products.filters.cancelAll") }}
                <v-btn icon @click="cancelFilters()">
                  <v-icon> mdi-cancel </v-icon>
                </v-btn>
              </v-col>
            </v-row>

            <v-row>
              <v-col
                ><h4>{{ $t("products.filters.powerType") }}</h4>
              </v-col>
            </v-row>

            <v-row>
              <v-col cols="12" sm="3" md="3" v-for="(item, index) in filters.powerType" :key="index" align="center">
                <v-checkbox @change="updateFilters()" v-model="item.value" :label="item.name" :prepend-icon="item.icon"></v-checkbox>
              </v-col>
            </v-row>

            <v-row>
              <v-col
                ><h4>{{ $t("products.filters.plugType") }}</h4></v-col
              >
            </v-row>

            <v-row>
              <v-col cols="12" sm="3" md="3" v-for="(item, index) in filters.plugType" :key="index" align="center">
                <v-checkbox @change="updateFilters()" v-model="item.value" :label="item.name" :prepend-icon="item.icon"></v-checkbox>
              </v-col>
            </v-row>

            <v-row>
              <v-col
                ><h4>{{ $t("products.filters.type") }}</h4></v-col
              >
            </v-row>

            <v-row>
              <v-col cols="12" sm="3" md="3" v-for="(item, index) in filters.type" :key="index" align="center">
                <v-checkbox @change="updateFilters()" v-model="item.value" :label="item.name" :prepend-icon="item.icon"></v-checkbox>
              </v-col>
            </v-row>

            <v-row>
              <v-col
                ><h4>{{ $t("products.filters.minPower") }}</h4></v-col
              >
            </v-row>

            <v-row>
              <v-col cols="12" sm="3" md="3" v-for="(item, index) in filters.minPower" :key="index" align="center">
                <v-slider @change="updateFilters()" v-model="item.value" :label="item.value + ' ' + item.name" hint="kW" max="300" min="0"></v-slider>
              </v-col>
            </v-row>
          </v-tab-item>
        </v-tabs>
      </v-col>
    </v-row>
  </div>
</template>

<script>
import ProductsSimpleTable from "../../components/products/SimpleTable.vue";

import * as MyPlaces from "../../components/products/places.js";
import * as MyFilters from "../../components/products/filters.js";
import { addToFavorites, loadFavorites, getFavoriteColor } from "../../components/products/favorites.js";

import { ServiceClient } from "../../service_client/service_client.js";

import { Icon, icon } from "leaflet";
import * as Vue2Leaflet from "vue2-leaflet";
import Vue2LeafletMarkerCluster from "vue2-leaflet-markercluster";

import iconUrl from "leaflet/dist/images/marker-icon.png";
import shadowUrl from "leaflet/dist/images/marker-shadow.png";

import { mapActions } from "vuex";

export default {
  name: "Products",

  components: {
    ProductsSimpleTable,
    "v-leaflet-control": Vue2Leaflet.LControl,
    "v-leaflet-control-scale": Vue2Leaflet.LControlScale,
    "v-leaflet-circle": Vue2Leaflet.LCircle,
    "v-leaflet-icon": Vue2Leaflet.LIcon,
    "v-leaflet-map": Vue2Leaflet.LMap,
    "v-leaflet-marker": Vue2Leaflet.LMarker,
    "v-leaflet-popup": Vue2Leaflet.LPopup,
    "v-leaflet-tilelayer": Vue2Leaflet.LTileLayer,
    "v-leaflet-tooltip": Vue2Leaflet.LTooltip,
    "v-leaflet-marker-cluster": Vue2LeafletMarkerCluster,
  },
  data() {
    let customicon = icon(Object.assign({}, Icon.Default.prototype.options, { iconUrl, shadowUrl }));
    return {
      ignoreBorders: false,

      showSearchButton: false,
      searchButtonIsLoading: false,
      previousCenter: null,

      tabs: 0,

      data: null,

      location: null,
      locations: [],
      locationsFiltered: [],

      favorites: [],

      icon: customicon,
      iconSize: 48,
      clusterOptions: {},

      map: null,

      url: "https://{s}.tile.openstreetmap.org/{z}/{x}/{y}.png",
      attributionOriginal: '<br/> &copy; <a target="_blank" href="http://osm.org/copyright">OpenStreetMap</a> contributors',
      attribution: '<br/> &copy; <a target="_blank" href="http://osm.org/copyright">OpenStreetMap</a> contributors',

      zoom: 14,
      center: [49.720928, 15.320435],

      bounds: {
        _southWest: {
          lat: 0,
          lng: 0,
        },
        _northEast: {
          lat: 0,
          lng: 0,
        },
      },

      myLocation: {
        auto: true,
        gps: [0, 0],
        radius: 14,
        color: "blue",
        accuracy: 0,
      },

      filters: MyFilters.filters,

      timer1: null,
      mobile: false,

      windowHeight: window.innerHeight,
      windowWidth: window.innerWidth,

      mapHeight: window.innerHeight,
      mapWidth: window.innerWidth,
    };
  },
  created() {
    this.getBreakpoints();
    this.getLocation();
  },
  mounted() {
    setTimeout(() => {
      this.$nextTick(() => {
        this.clusterOptions = { disableClusteringAtZoom: 11 };
      });
    }, 5000);

    this.favorites = this.loadFavorites();
    this.loadFilters();

    this.timer1 = setInterval(() => {
      this.getLocation();
    }, 5000);

    setTimeout(() => {
      this.getData();
    }, 1000);

    this.$nextTick(() => {
      window.addEventListener("resize", this.onResize);
    });
  },

  computed: {
    dynamicSize() {
      return [this.iconSize, this.iconSize * 1.25];
    },
    dynamicAnchor() {
      return [this.iconSize / 2, this.iconSize * 1.25];
    },
  },
  methods: {
    ...mapActions({
      vuexUpdatePointType: "point/updateType",
      vuexUpdatePointProjectName: "point/updateProjectName",
      vuexUpdatePointObjectName: "point/updateObjectName",
      vuexUpdatePointSerialNumber: "point/updateSerialNumber",
    }),
    loadFavorites,
    addToFavorites,
    getFavoriteColor,
    isCenterChanged(oldCenter, newCenter) {
      return Math.abs(oldCenter.lat - newCenter.lat) > 0.001 || Math.abs(oldCenter.lng - newCenter.lng) > 0.001;
    },

    triggerSearchStations() {
      if (this.searchButtonIsLoading === true) {
        return;
      }
      this.searchButtonIsLoading = true;

      setTimeout(() => {
        this.searchStations();
        this.searchButtonIsLoading = false;
      }, 1500);
    },
    searchStations() {
      this.showSearchButton = false;
      this.getData();
    },
    locationCustomFilter(item, queryText, itemText) {
      const searchText = queryText.toLowerCase();

      const projectName = item.projectName.toLowerCase();
      const objectName = item.objectName.toLowerCase();
      const sn = item.sn.toLowerCase();
      const type = item.type.toLowerCase();

      return projectName.includes(searchText) || objectName.includes(searchText) || sn.includes(searchText) || type.includes(searchText);
    },

    /*     emitAddToFavorite(item) {
      if (this.disabled === true) {
        return;
      }
      this.blockActions();
      this.$emit("eventAddToFavorite", item);
      this.showSnackbar(this.$i18n.t("products.table.object") + " " + `${item.objectName} - ${item.sn}` + " " + this.$i18n.t("products.table.addToFavorite"));
    },
    emitSetPoint(item) {
      if (this.disabled === true) {
        return;
      }
      this.blockActions();
      this.$emit("eventSetPoint", item);
      this.showSnackbar(this.$i18n.t("products.table.object") + " " + `${item.objectName} - ${item.sn}` + " " + this.$i18n.t("products.table.setPoint"));
    }, */

    /*     mapAddToFavorite() {

    },
    mapSetStation() {
      
    } */
    handleSetPoint(item) {
      this.setPoint(item);
    },
    handleAddToFavorite(item) {
      this.addToFavorites(item, this.favorites);
    },
    setPoint(item) {
      let parameters = "";

      let id1 = 0;
      let id2 = 0;

      if ("stationId" in item) {
        if (item.stationId != null) {
          id1 = 1;
        }
      }
      if ("unitId" in item) {
        if (item.unitId != null) {
          id2 = 1;
        }
      }

      if (id1) {
        parameters = "?" + "stationId" + "=" + item.stationId;
      } else if (id2) {
        parameters = "?" + "unitId" + "=" + item.unitId;
      } else {
        return;
      }

      ServiceClient.dataCore(ServiceClient.getAPIUrl("point"), null, parameters, "GET")
        .then((data) => {
          let type;

          if ("type" in data) {
            type = data.type;

            this.vuexUpdatePointType(type);
          } else {
            type = "";

            this.vuexUpdatePointType(type);
          }

          if ("projectName" in data) {
            this.vuexUpdatePointProjectName(data.projectName);
          }
          if ("objectName" in data) {
            this.vuexUpdatePointObjectName(data.objectName);
          }
          if ("sn" in data) {
            this.vuexUpdatePointSerialNumber(data.sn);
          }

          if (type === MyFilters.type[0]) {
            this.$router.push({ path: "evcs-dashboard" });
          } else if (type === MyFilters.type[1]) {
            this.$router.push({ path: "eess-basic" });
          } else if (type === MyFilters.type[2]) {
            this.$router.push({ path: "escb-dashboard" });
          }
        })
        .catch((error) => {
          ServiceClient.handleUnauthorized(error, this.$router);
        });
    },
    onChangeLocation() {
      this.myLocation.auto = false;

      if (this.location == null) {
        return;
      }

      const indexFound = this.locationsFiltered.findIndex((location) => location.sn === this.location.sn);

      if (indexFound === -1) {
        return;
      }

      if (this.zoom < 17) {
        this.zoom = 17;
      }

      this.center = this.locationsFiltered[indexFound].latLng;

      let temp = this.locationsFiltered[indexFound];
      this.$set(this.locationsFiltered, indexFound, this.locationsFiltered[0]);
      this.$set(this.locationsFiltered, 0, temp);
    },
    onClickMarker(item) {
      this.myLocation.auto = false;
      this.center = item.latLng;
    },
    click(e) {
      this.myLocation.auto = false;
    },
    ready(e) {},
    zoomUpdated(zoom) {
      this.myLocation.auto = false;
      this.zoom = zoom;
      this.showCoordinatesInAttributes();
    },
    centerUpdated(center) {
      this.myLocation.auto = false;
      this.center = center;
      this.showCoordinatesInAttributes();
    },
    boundsUpdated(bounds) {
      this.myLocation.auto = false;
      this.bounds = bounds;
      this.showCoordinatesInAttributes();
    },
    showCoordinatesInAttributes() {
      let zoom = "<br/> Z: " + this.zoom;
      let center = "<br/> C: [" + Math.round(JSON.stringify(this.center.lat) * 100) / 100 + ";" + Math.round(JSON.stringify(this.center.lng) * 100) / 100 + "] ";
      let SW = "<br/> SW: [" + Math.round(JSON.stringify(this.bounds._southWest.lat) * 100) / 100 + ";" + Math.round(JSON.stringify(this.bounds._southWest.lng) * 100) / 100 + "]";
      let NE = "<br/> NE: [" + Math.round(JSON.stringify(this.bounds._northEast.lat) * 100) / 100 + ";" + Math.round(JSON.stringify(this.bounds._northEast.lng) * 100) / 100 + "]";

      this.attribution = zoom + center + SW + NE + this.attributionOriginal;
    },
    getData() {
      let parameters = "";

      if (this.ignoreBorders === false) {
        parameters += "?zoom=" + this.zoom;
        parameters += "&_southWestLat=" + JSON.stringify(this.bounds._southWest.lat);
        parameters += "&_southWestLng=" + JSON.stringify(this.bounds._southWest.lng);
        parameters += "&_northEastLat=" + JSON.stringify(this.bounds._northEast.lat);
        parameters += "&_northEastLng=" + JSON.stringify(this.bounds._northEast.lng);
      }

      ServiceClient.dataCore(ServiceClient.getAPIUrl("places"), null, parameters, "GET")
        .then((data) => {
          this.data = data;

          this.processData(this.data);
        })
        .catch((error) => {
          this.data = ServiceClient.handleUnauthorized(error, this.$router);
        });
    },
    processData(data) {
      this.locations = MyPlaces.IterateAndMergeAllThePlacesAndPointsInto1DArray(data);

      MyPlaces.IteratePointsAndSetIcons(this.locations);

      // TODO Posílat stateDesc, na základě toho rozlišuju ikonky

      this.locationsFiltered = this.applyFilters(this.locations);
    },
    applyFilters(locations) {
      locations = this.applyFilter(this.filters.plugType, locations, "plugType");
      locations = this.applyFilter(this.filters.powerType, locations, "powerType");
      locations = this.applyFilter(this.filters.minPower, locations, "minPower");
      locations = this.applyFilter(this.filters.type, locations, "type");

      return locations;
    },
    applyFilter(filters, locations, label) {
      let activeFiltersArray = [];

      activeFiltersArray = filters.filter(this.isFilterActive);

      if (activeFiltersArray.length != 0) {
        locations = locations.filter(function (item) {
          for (let index = 0; index < activeFiltersArray.length; index++) {
            if (label == "plugType") {
              if (item.plugType == activeFiltersArray[index].name) {
                return true;
              }
            } else if (label == "powerType") {
              if (item.powerType == activeFiltersArray[index].name) {
                return true;
              }
            } else if (label == "minPower") {
              if (item.minPower >= activeFiltersArray[index].value) {
                return true;
              }
            } else if (label == "type") {
              if (item.type == activeFiltersArray[index].name) {
                return true;
              }
            }
          }

          return false;
        });
      }
      return locations;
    },
    isFilterActive(item) {
      if (typeof item.value == "boolean") {
        // Filter is active (Bool)
        if (item.value == true) {
          return true;
        }
      } else if (typeof item.value == "number") {
        // Filter is active (Number)
        if (item.value != 0) {
          return true;
        }
      }

      return false;
    },
    updateFilters() {
      let obj = {
        plugType: [],
        powerType: [],
        minPower: [],
        type: [],
      };

      this.$set(obj, "plugType", this.updateFilter(this.filters.plugType));
      this.$set(obj, "powerType", this.updateFilter(this.filters.powerType));
      this.$set(obj, "minPower", this.updateFilter(this.filters.minPower));
      this.$set(obj, "type", this.updateFilter(this.filters.type));

      localStorage.setItem("filter", JSON.stringify(obj));

      this.locationsFiltered = this.applyFilters(this.locations);
    },
    updateFilter(category) {
      let array = [];
      let obj = {};

      for (let index = 0; index < category.length; index++) {
        obj = {
          name: category[index].name,
          value: category[index].value,
        };

        array.push(obj);
      }

      return array;
    },
    loadFilters() {
      if (localStorage.getItem("filter") === null) {
        return;
      }

      let tmp = JSON.parse(localStorage.getItem("filter"));

      this.loadFilter(this.filters.plugType, tmp.plugType);
      this.loadFilter(this.filters.powerType, tmp.powerType);
      this.loadFilter(this.filters.minPower, tmp.minPower);
      this.loadFilter(this.filters.type, tmp.type);
    },
    loadFilter(destination, source) {
      for (let index = 0; index < source.length; index++) {
        this.$set(destination[index], "value", source[index].value);
      }
    },
    cancelFilters() {
      this.cancelFilter(this.filters.plugType, false);
      this.cancelFilter(this.filters.powerType, false);
      this.cancelFilter(this.filters.minPower, 0);
      this.cancelFilter(this.filters.type, false);

      this.updateFilters();
    },
    cancelFilter(array, init) {
      for (let index = 0; index < array.length; index++) {
        this.$set(array[index], "value", init);
      }
    },
    mapOnReady() {
      this.map = this.$refs.myMap.mapObject;
    },
    getLocation() {
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(this.showPosition);
      }
    },
    showPosition(position) {
      this.myLocation.gps = [position.coords.latitude, position.coords.longitude];

      if (this.myLocation.auto === true) {
        this.center = this.myLocation.gps;
      }

      this.myLocation.accuracy = Math.round(position.coords.accuracy * 100) / 100;
    },
    getBreakpoints() {
      this.windowHeight = window.innerHeight;
      this.windowWidth = window.innerWidth;

      if (this.windowWidth <= 768) {
        this.mobile = true;
      } else {
        this.mobile = false;
      }

      if (this.mobile == true) {
        this.mapHeight = this.windowHeight - 80 - 75;
        this.mapWidth = this.windowWidth - 34 - 16;
      } else {
        this.mapHeight = this.windowHeight - 90 - 75;
        this.mapWidth = this.windowWidth - 250 - 55;
      }
    },
    onResize() {
      this.getBreakpoints();
    },
  },
  beforeDestroy() {
    clearInterval(this.timer1);

    window.removeEventListener("resize", this.onResize);
  },
  destroyed() {},
  watch: {
    center(newCenter) {
      if (!this.previousCenter || this.isCenterChanged(this.previousCenter, newCenter)) {
        this.showSearchButton = true;
      }
      this.previousCenter = { ...newCenter };
    },
    zoom() {},
    bounds() {},
    tabs(newTab, oldTab) {
      if (oldTab === 0 && (newTab === 1 || newTab === 2)) {
        this.ignoreBorders = true;
        this.getData();
      } else if ((oldTab === 1 || oldTab === 2) && newTab === 0) {
        this.ignoreBorders = false;
        this.getData();
      }
    },
  },
};
</script>

<style scoped>
@import "~leaflet/dist/leaflet.css";
@import "~leaflet.markercluster/dist/MarkerCluster.css";
@import "~leaflet.markercluster/dist/MarkerCluster.Default.css";
html,
body {
  height: 100%;
  margin: 0;
}
</style>
